import Vue from 'vue'
import VueRouter from 'vue-router'
import { getToken, getUser } from '@/utils/auth'

// 布局组件
import MainLayout from '@/layouts/MainLayout.vue'
import LoginLayout from '@/layouts/LoginLayout.vue'

// 控制中心页面
import Dashboard from '@/views/Dashboard.vue'

// 用户管理相关页面
import UserList from '@/views/user/UserList.vue'
import UserDetail from '@/views/user/UserDetail.vue'
import UserCreate from '@/views/user/UserCreate.vue'
import UserEdit from '@/views/user/UserEdit.vue'
import UserGroupList from '@/views/user/UserGroupList.vue'
import UserGroupDetail from '@/views/user/UserGroupDetail.vue'
import UserGroupCreate from '@/views/user/UserGroupCreate.vue'
import UserGroupEdit from '@/views/user/UserGroupEdit.vue'
import UserGroupAddUsers from '@/views/user/UserGroupAddUsers.vue'
import UserRole from '@/views/user/UserRole.vue'
import LevelManagement from '@/views/system/LevelManagement.vue'
import PointsManagement from '@/views/user/PointsManagement.vue'

// 硬件设备管理相关页面
import DeviceList from '@/views/device/DeviceList.vue'
import DeviceDetail from '@/views/device/DeviceDetail.vue'
import DeviceEdit from '@/views/device/DeviceEdit.vue'
import DeviceAdd from '@/views/device/DeviceAdd.vue'
import DeviceLogs from '@/views/device/DeviceLogs.vue'
import DeviceUpgrade from '@/views/device/DeviceUpgrade.vue'

// 资源分配与监控管理相关页面
import ResourceDashboard from '@/views/resource/ResourceDashboard.vue'
import ResourceQuota from '@/views/resource/ResourceQuota.vue'
import QuotaAllocation from '@/views/resource/QuotaAllocation.vue'
import ResourceReport from '@/views/resource/ResourceReport.vue'
import ModelList from '@/views/resource/ModelList.vue'
import QuotaPolicy from '@/views/resource/QuotaPolicy.vue'

// 系统管理相关页面
import LogManagement from '@/views/system/LogManagement.vue'
import NotificationManagement from '@/views/system/NotificationManagement.vue'
import SystemConfig from '@/views/system/SystemConfig.vue'
import AlarmManagement from '@/views/system/AlarmManagement.vue'
import JobManagement from '@/views/system/JobManagement.vue'
import JobLog from '@/views/system/JobLog.vue'
import DictManagement from '@/views/system/DictManagement.vue'
import SystemParams from '@/views/system/SystemParams.vue'
import DeviceOta from '@/views/device/DeviceOta.vue'
import ModelProvider from '@/views/system/ModelProvider.vue'

import ConsultationList from '@/views/cus/ConsultationList.vue'

import CommunityList from '@/views/activity/CommunityList.vue'
import CommunityEdit from '@/views/activity/CommunityEdit.vue'
import CommunityDetail from '@/views/activity/CommunityDetail.vue'
import MemorialList from '@/views/activity/MemorialList.vue'
import MemorialEdit from '@/views/activity/MemorialEdit.vue'
import RightsShopList from '@/views/activity/RightsShopList.vue'
import RightsShopEdit from '@/views/activity/RightsShopEdit.vue'
import RightsExchangeList from '@/views/activity/RightsExchangeList.vue'

// 用户认证相关页面
import Login from '@/views/auth/Login.vue'
import Register from '@/views/auth/Register.vue'
import ForgotPassword from '@/views/auth/ForgotPassword.vue'
import ResetPassword from '@/views/auth/ResetPassword.vue'

Vue.use(VueRouter)

// 判断是否有权限
function checkPermission(to) {
  const user = getUser()
  if (!user) return false
  
  // 超级管理员拥有所有权限
  if (user.superAdmin === 1) return true
  
  const requiresPermissions = to.meta.permissions || []
  if (requiresPermissions.length === 0) return true
  
  const userPermissions = Array.isArray(user.permissions) ? user.permissions : []
  const matchMode = to.meta.permissionMode === 'any' ? 'any' : 'all'
  if (matchMode === 'any') {
    return requiresPermissions.some(p => userPermissions.includes(p) || userPermissions.includes('*:*:*'))
  }
  return requiresPermissions.every(p => userPermissions.includes(p) || userPermissions.includes('*:*:*'))
}

const routes = [
  // 认证相关路由 - 使用登录布局
  {
    path: '/login',
    name: 'Login',
    component: LoginLayout,
    children: [
      { path: '', component: Login, meta: { requiresAuth: false } },
      { path: 'register', component: Register, meta: { requiresAuth: false } },
      { path: 'forgot-password', component: ForgotPassword, meta: { requiresAuth: false } },
      { path: 'reset-password/:token', component: ResetPassword, meta: { requiresAuth: false } }
    ]
  },
  
  // 默认重定向到仪表板
  { path: '/', redirect: '/dashboard' },
  
  // 主应用路由 - 使用主布局
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      // 仪表板
      { path: 'dashboard', name: 'Dashboard', component: Dashboard, meta: { title: '控制中心', permissions: ['sys:dashboard:view'] } },

      // 数据分析路由
      { path: 'analysis/overview', name: 'AnalysisOverview', component: () => import('@/views/analysis/Overview.vue'), meta: { title: '数据总览', permissions: ['sys:analysis:overview'] } },
      { path: 'analysis/user', name: 'AnalysisUser', component: () => import('@/views/analysis/UserAnalysis.vue'), meta: { title: '用户数据分析', permissions: ['sys:analysis:user'] } },
      { path: 'analysis/points', name: 'AnalysisPoints', component: () => import('@/views/analysis/PointsAnalysis.vue'), meta: { title: '积分权益分析', permissions: ['sys:analysis:points'] } },
      { path: 'analysis/product', name: 'AnalysisProduct', component: () => import('@/views/analysis/ProductAnalysis.vue'), meta: { title: '产品数据分析', permissions: ['sys:analysis:product'] } },
      { path: 'analysis/collection', name: 'AnalysisCollection', component: () => import('@/views/analysis/CollectionAnalysis.vue'), meta: { title: '数字藏品分析', permissions: ['sys:analysis:collection'] } },
      { path: 'analysis/badge', name: 'AnalysisBadge', component: () => import('@/views/analysis/BadgeAnalysis.vue'), meta: { title: '徽章活动分析', permissions: ['sys:analysis:badge'] } },
      { path: 'analysis/activity', name: 'AnalysisUserActivity', component: () => import('@/views/analysis/UserActivity.vue'), meta: { title: '日活用户分析', permissions: ['sys:analysis:activity'] } },

      // 用户管理路由
      { path: 'user', name: 'UserList', component: UserList, meta: { title: '用户列表', permissions: ['sys:user:list'] } },
      { path: 'user/detail/:id', name: 'UserDetail', component: UserDetail, props: true, meta: { title: '用户详情', permissions: ['sys:user:list', 'sys:user:info'], activeMenu: 'user-list', keepAlive: true } },
      { path: 'user/create', name: 'UserCreate', component: UserCreate, meta: { title: '创建用户', permissions: ['sys:user:save'], activeMenu: 'user-list' } },
      { path: 'user/edit/:id', name: 'UserEdit', component: UserEdit, props: true, meta: { title: '编辑用户', permissions: ['sys:user:update'], activeMenu: 'user-list' } },
      { path: 'profile', name: 'UserProfile', component: () => import('@/views/user/UserProfile.vue'), meta: { title: '个人中心', permissions: [], activeMenu: 'profile', keepAlive: true } },
      { path: 'settings', name: 'UserSettings', component: () => import('@/views/system/UserSettings.vue'), meta: { title: '系统设置', permissions: [], activeMenu: 'settings', keepAlive: true } },
      
      // 用户分组管理路由
      { path: 'user/group', name: 'UserGroupList', component: UserGroupList, meta: { title: '用户分组', permissions: ['sys:usergroup:list'] } },
      { path: 'user/group/detail/:id', name: 'UserGroupDetail', component: UserGroupDetail, props: true, meta: { title: '用户分组详情', permissions: ['sys:usergroup:list', 'sys:usergroup:info'], activeMenu: 'user-group' } },
      { path: 'user/group/create', name: 'UserGroupCreate', component: UserGroupCreate, meta: { title: '创建用户分组', permissions: ['sys:usergroup:save'], activeMenu: 'user-group' } },
      { path: 'user/group/edit/:id', name: 'UserGroupEdit', component: UserGroupEdit, props: true, meta: { title: '编辑用户分组', permissions: ['sys:usergroup:update'], activeMenu: 'user-group' } },
      { path: 'user/group/:id/add-users', name: 'UserGroupAddUsers', component: UserGroupAddUsers, props: true, meta: { title: '添加用户到分组', permissions: ['sys:usergroup:update'], activeMenu: 'user-group' } },
      { path: 'user/level', name: 'LevelManagement', component: LevelManagement, meta: { title: '等级管理', permissions: ['sys:level:list'] } },
      { path: 'user/points', name: 'PointsManagement', component: PointsManagement, meta: { title: '积分管理', permissions: ['sys:points:list'] } },
      { path: 'user/role', name: 'UserRole', component: UserRole, meta: { title: '角色权限', permissions: ['sys:role:list', 'sys:role:view'], permissionMode: 'any' } },
      { path: 'user/permission', name: 'PermissionManagement', component: () => import('@/views/user/PermissionManagement.vue'), meta: { title: '权限管理', permissions: ['sys:menu:list'] } },
      
      // 产品管理路由 (Product Profile)
      { path: 'product', name: 'ProductList', component: () => import('@/views/product/ProductList.vue'), meta: { title: '产品管理', permissions: ['sys:product:list'] } },
      { path: 'product/series', name: 'SeriesList', component: () => import('@/views/product/series/SeriesList.vue'), meta: { title: '系列管理', permissions: ['sys:series:list'] } },
      { path: 'product/series/add', name: 'SeriesAdd', component: () => import('@/views/product/series/SeriesEdit.vue'), meta: { title: '新增系列', permissions: ['sys:series:save'], activeMenu: 'series-list' } },
      { path: 'product/series/edit/:id', name: 'SeriesEdit', component: () => import('@/views/product/series/SeriesEdit.vue'), props: true, meta: { title: '编辑系列', permissions: ['sys:series:update'], activeMenu: 'series-list' } },
      { path: 'product/series/detail/:id', name: 'SeriesDetail', component: () => import('@/views/product/series/SeriesDetail.vue'), props: true, meta: { title: '系列详情', permissions: ['sys:series:info'], activeMenu: 'series-list' } },
      { path: 'product/sku-manage', name: 'SkuList', component: () => import('@/views/product/sku/SkuList.vue'), meta: { title: 'SKU管理', permissions: ['sku:sku:list'] } },
      { path: 'product/sku-manage/add', name: 'SkuAdd', component: () => import('@/views/product/sku/SkuAdd.vue'), meta: { title: '新增SKU', permissions: ['sku:sku:save'], activeMenu: 'sku-manage' } },
      { path: 'product/sku-manage/edit/:id', name: 'SkuEdit', component: () => import('@/views/product/sku/SkuEdit.vue'), props: true, meta: { title: '编辑SKU', permissions: ['sku:sku:update'], activeMenu: 'sku-manage' } },
      { path: 'product/sku-manage/detail/:id', name: 'SkuDetail', component: () => import('@/views/product/sku/SkuDetail.vue'), props: true, meta: { title: 'SKU详情', permissions: ['sku:sku:list'], activeMenu: 'sku-manage' } },
      { path: 'product/sku-rule', name: 'SkuRuleList', component: () => import('@/views/product/sku/SkuRuleList.vue'), meta: { title: 'SKU规则', permissions: ['sku:rule:list'] } },
      { path: 'product/digital-collection', name: 'DigitalCollectionList', component: () => import('@/views/product/collection/DigitalCollectionList.vue'), meta: { title: '数字藏品', permissions: ['sys:collection:list'] } },
      { path: 'product/add', name: 'ProductAdd', component: () => import('@/views/product/ProductEdit.vue'), meta: { title: '新增产品档案', permissions: ['sys:product:save'], activeMenu: 'product-list' } },
      { path: 'product/:id', name: 'ProductDetail', component: () => import('@/views/product/ProductDetail.vue'), meta: { title: '产品详情', permissions: ['sys:product:info'], activeMenu: 'product-list' } },
      { path: 'product/:id/edit', name: 'ProductEdit', component: () => import('@/views/product/ProductEdit.vue'), meta: { title: '编辑产品档案', permissions: ['sys:product:update'], activeMenu: 'product-list' } },
      
      // 硬件设备管理路由
      { path: 'device', name: 'DeviceList', component: DeviceList, meta: { title: '设备列表', permissions: ['sys:device:list'] } },
      { path: 'device/detail/:id', name: 'DeviceDetail', component: DeviceDetail, props: true, meta: { title: '设备详情', permissions: ['sys:device:info'], activeMenu: 'device-list' } },
      { path: 'device/edit/:id', name: 'DeviceEdit', component: DeviceEdit, props: true, meta: { title: '编辑设备', permissions: ['sys:device:update'], activeMenu: 'device-list' } },
      { path: 'device/add', name: 'DeviceAdd', component: DeviceAdd, meta: { title: '绑定设备', permissions: ['sys:device:update'], activeMenu: 'device-list' } },
      { path: 'device/logs/:id', name: 'DeviceLogs', component: DeviceLogs, props: true, meta: { title: '设备日志', permissions: ['sys:device:info'], activeMenu: 'device-list' } },
      { path: 'device/upgrade', name: 'DeviceUpgrade', component: DeviceUpgrade, meta: { title: '固件升级', permissions: ['sys:device:update'] } },
      { path: 'device/ota', name: 'OtaManagement', component: DeviceOta, meta: { title: 'OTA管理', permissions: ['sys:ota:list'] } },
      { path: 'device/peripheral/list', name: 'NfcManagement', component: () => import('@/views/device/PeripheralList.vue'), meta: { title: 'NFC管理', permissions: ['sys:peripheral:list'] } },
      { path: 'device/peripheral/nfc-add', name: 'NfcCardAdd', component: () => import('@/views/device/NfcCardAdd.vue'), meta: { title: '新增NFC卡', permissions: ['sys:peripheral:save'], activeMenu: 'peripheral-list' } },
      { path: 'device/peripheral/nfc-detail/:nfcUuid', name: 'NfcCardDetail', component: () => import('@/views/device/NfcCardDetail.vue'), props: true, meta: { title: 'NFC卡详情', permissions: ['sys:peripheral:info'], activeMenu: 'peripheral-list' } },
      { path: 'device/peripheral/detail/:id', name: 'PeripheralDetail', component: () => import('@/views/device/PeripheralDetail.vue'), props: true, meta: { title: 'NFC卡详情', permissions: ['sys:peripheral:info'], activeMenu: 'peripheral-list' } },
      { path: 'device/peripheral/add', name: 'PeripheralAdd', component: () => import('@/views/device/PeripheralAdd.vue'), meta: { title: '绑定NFC卡', permissions: ['sys:peripheral:update'], activeMenu: 'peripheral-list' } },
      { path: 'device/peripheral/edit/:id', name: 'PeripheralEdit', component: () => import('@/views/device/PeripheralEdit.vue'), props: true, meta: { title: '编辑NFC卡', permissions: ['sys:peripheral:update'], activeMenu: 'peripheral-list' } },

      
      // 资源分配与监控管理路由
      { path: 'resource/dashboard', name: 'ResourceDashboard', component: ResourceDashboard, meta: { title: '资源概览与监控', permissions: ['sys:resource:view'] } },
      { path: 'resource/models', name: 'ModelList', component: ModelList, meta: { title: '模型管理', permissions: ['sys:model:list'] } },
      { path: 'resource/provider', name: 'ModelProvider', component: ModelProvider, meta: { title: '供应器管理', permissions: ['sys:modelprovider:list'] } },
      { path: 'resource/models/add/:type', name: 'ModelAdd', component: () => import('@/views/resource/ModelAdd.vue'), meta: { title: '添加模型', permissions: ['sys:model:save'], activeMenu: 'model-list' } },
      { path: 'resource/models/edit/:id/:type', name: 'ModelEdit', component: () => import('@/views/resource/ModelEdit.vue'), props: true, meta: { title: '编辑模型', permissions: ['sys:model:update'], activeMenu: 'model-list' } },
      { path: 'resource/models/detail/:id/:type', name: 'ModelDetail', component: () => import('@/views/resource/ModelDetail.vue'), props: true, meta: { title: '模型详情', permissions: ['sys:model:info'], activeMenu: 'model-list' } },
      { path: 'resource/quota', name: 'ResourceQuota', component: ResourceQuota, meta: { title: '配额管理', permissions: ['sys:resource:allocate'] } },
      { path: 'resource/quota/policy', name: 'QuotaPolicy', component: QuotaPolicy, meta: { title: '配额策略', permissions: ['sys:quota:policy'], activeMenu: 'quota-policy' } },
      { path: 'resource/quota/logs', name: 'QuotaLogs', component: () => import('@/views/resource/QuotaLogs.vue'), meta: { title: '配额日志', permissions: ['sys:quota:logs'], activeMenu: 'quota-logs' } },
      { path: 'resource/usage', name: 'ResourceQuotaUsage', component: () => import('@/views/resource/ResourceQuotaUsage.vue'), meta: { title: '配额详情', permissions: ['sys:quota:info'], activeMenu: 'resource-quota' } },
      { path: 'resource/quota/allocate', name: 'QuotaAllocation', component: QuotaAllocation, meta: { title: '配额分配', permissions: ['sys:quota:allocate'], activeMenu: 'resource-quota' } },
      { path: 'resource/report', name: 'ResourceReport', component: ResourceReport, meta: { title: '使用报表', permissions: ['sys:resource:report'] } },

      // 智能体管理路由
      { path: 'agent/list', name: 'AgentList', component: () => import('@/views/agent/AgentList.vue'), meta: { title: '智能体列表', permissions: ['sys:agent:list'] } },
      { path: 'agent/create', name: 'AgentCreate', component: () => import('@/views/agent/AgentEdit.vue'), meta: { title: '创建智能体', permissions: ['sys:agent:save'], activeMenu: 'agent-list' } },
      { path: 'agent/edit/:id', name: 'AgentEdit', component: () => import('@/views/agent/AgentEdit.vue'), props: true, meta: { title: '编辑智能体', permissions: ['sys:agent:update'], activeMenu: 'agent-list' } },
      { path: 'agent/detail/:id', name: 'AgentDetail', component: () => import('@/views/agent/AgentDetail.vue'), props: true, meta: { title: '智能体详情', permissions: ['sys:agent:list'], activeMenu: 'agent-list' } },
      { path: 'agent/template', name: 'AgentTemplate', component: () => import('@/views/agent/AgentTemplate.vue'), meta: { title: '智能体模板', permissions: ['sys:agenttemplate:list'] } },
      { path: 'agent/template/create', name: 'AgentTemplateCreate', component: () => import('@/views/agent/AgentTemplateEdit.vue'), meta: { title: '创建智能体模板', permissions: ['sys:agenttemplate:save'], activeMenu: 'agent-template' } },
      { path: 'agent/template/edit/:id', name: 'AgentTemplateEdit', component: () => import('@/views/agent/AgentTemplateEdit.vue'), props: true, meta: { title: '编辑智能体模板', permissions: ['sys:agenttemplate:update'], activeMenu: 'agent-template' } },
      { path: 'agent/template/detail/:id', name: 'AgentTemplateDetail', component: () => import('@/views/agent/AgentTemplateDetail.vue'), props: true, meta: { title: '智能体模板详情', permissions: ['sys:agenttemplate:list'], activeMenu: 'agent-template' } },
      { path: 'agent/plugin', name: 'AgentPlugin', component: () => import('@/views/agent/AgentPlugin.vue'), meta: { title: '智能体插件', permissions: ['sys:agentplugin:list'] } },
      { path: 'agent/chat', name: 'ChatHistory', component: () => import('@/views/agent/ChatHistory.vue'), meta: { title: '聊天记录', permissions: ['sys:chat:list'] } },

      // 内容资产管理路由
      { path: 'cms/assets', name: 'AssetList', component: () => import('@/views/cms/AssetList.vue'), meta: { title: '资产管理', permissions: ['cms:asset:list'] } },
      { path: 'cms/assets/:id', name: 'AssetDetail', component: () => import('@/views/cms/AssetDetail.vue'), meta: { title: '资产详情', permissions: ['cms:asset:list', 'cms:asset:info'], activeMenu: 'cms-asset-list' } },
      { path: 'cms/tags', name: 'TagManagement', component: () => import('@/views/cms/TagManagement.vue'), meta: { title: '标签管理', permissions: ['cms:tag:list'], activeMenu: 'cms-tag-management' } },
      { path: 'cms/themes', name: 'ThemeList', component: () => import('@/views/cms/ThemeList.vue'), meta: { title: '主题管理', permissions: ['cms:theme:page'] } },
      { path: 'cms/themes/:id', name: 'ThemeDetail', component: () => import('@/views/cms/ThemeDetail.vue'), meta: { title: '主题详情', permissions: ['cms:theme:page', 'cms:theme:info'], activeMenu: 'cms-theme-list' } },
      { path: 'cms/documents', name: 'DocumentList', component: () => import('@/views/cms/DocumentList.vue'), meta: { title: '文档管理', permissions: ['cms:document:list'] } },

      // 灯光效果管理路由
      { path: 'light/effect', name: 'LightEffectList', component: () => import('@/views/light/LightEffectList.vue'), meta: { title: '灯效管理', permissions: ['sys:light:list'], activeMenu: 'light-effect-list' } },

      // 活动管理路由
      { path: 'activity/community', name: 'CommunityList', component: CommunityList, meta: { title: '社区活动', permissions: ['activity:community:list'] } },
      { path: 'activity/community/add', name: 'CommunityAdd', component: CommunityEdit, meta: { title: '发布活动', permissions: ['activity:community:save'], activeMenu: 'activity-community' } },
      { path: 'activity/community/edit/:id', name: 'CommunityEditPage', component: CommunityEdit, props: true, meta: { title: '编辑活动', permissions: ['activity:community:update'], activeMenu: 'activity-community' } },
      { path: 'activity/community/detail/:id', name: 'CommunityDetailPage', component: CommunityDetail, props: true, meta: { title: '活动详情', permissions: ['activity:community:info'], activeMenu: 'activity-community' } },
      { path: 'activity/memorial', name: 'MemorialList', component: MemorialList, meta: { title: '活动纪念', permissions: ['activity:memorial:list'] } },
      { path: 'activity/memorial/add', name: 'MemorialAdd', component: MemorialEdit, meta: { title: '创建纪念', permissions: ['activity:memorial:save'], activeMenu: 'activity-memorial' } },
      { path: 'activity/memorial/edit/:id', name: 'MemorialEditPage', component: MemorialEdit, props: true, meta: { title: '编辑纪念', permissions: ['activity:memorial:update'], activeMenu: 'activity-memorial' } },
      { path: 'activity/shop', name: 'RightsShopList', component: RightsShopList, meta: { title: '权益商店', permissions: ['activity:shop:list'] } },
      { path: 'activity/shop/add', name: 'RightsShopAdd', component: RightsShopEdit, meta: { title: '创建权益', permissions: ['activity:shop:save'], activeMenu: 'activity-shop' } },
      { path: 'activity/shop/edit/:id', name: 'RightsShopEditPage', component: RightsShopEdit, props: true, meta: { title: '编辑权益', permissions: ['activity:shop:update'], activeMenu: 'activity-shop' } },
      { path: 'activity/shop/exchange', name: 'RightsExchangeList', component: RightsExchangeList, meta: { title: '兑换记录', permissions: ['activity:shop:list'] } },

      // 售后服务路由
      { path: 'aftersales/consultation', name: 'ConsultationList', component: ConsultationList, meta: { title: '客诉管理', permissions: ['cus:consultation:list'] } },

      // 系统管理路由
      { path: 'system/log', name: 'LogManagement', component: LogManagement, meta: { title: '日志管理', permissions: ['sys:log:list'] } },
      { path: 'system/notification', name: 'NotificationManagement', component: NotificationManagement, meta: { title: '通知管理', permissions: ['sys:notification:page'] } },
      { path: 'system/alarm', name: 'AlarmManagement', component: AlarmManagement, meta: { title: '告警管理', permissions: ['sys:alarm:log:page'] } },
      { path: 'system/job', name: 'JobManagement', component: JobManagement, meta: { title: '定时任务', permissions: ['sys:job:list'] } },
      { path: 'system/job-log', name: 'JobLog', component: JobLog, meta: { title: '任务日志', permissions: ['sys:job:log'], activeMenu: 'system-job' } },
      { path: 'system/dict', name: 'DictManagement', component: DictManagement, meta: { title: '字典管理', permissions: ['sys:dict:list'] } },
      { path: 'system/params', name: 'SystemParams', component: SystemParams, meta: { title: '参数管理', permissions: ['sys:params:page'] } },
      { path: 'system/monitor', name: 'ServerMonitor', component: () => import('@/views/system/ServerMonitor.vue'), meta: { title: '服务器监控', permissions: ['sys:monitor:list'] } },
      { path: 'system/config', name: 'SystemConfig', component: SystemConfig, meta: { title: '系统配置', permissions: ['sys:config:info'] } }
    ]
  },
  
  // 错误页面
  { path: '/403', component: () => import('@/views/403.vue'), meta: { requiresAuth: false, title: '无权限访问' } },
  { path: '/404', component: () => import('@/views/404.vue') },
  { path: '*', redirect: '/404' }
]

const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace

// 重写 push 方法，捕获重复导航或重定向错误
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (err && err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
      return Promise.reject(err)
    }
  })
}

// 重写 replace 方法，捕获重复导航或重定向错误
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
  return originalReplace.call(this, location).catch(err => {
    if (err && err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
      return Promise.reject(err)
    }
  })
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  // 设置页面标题
  document.title = to.meta.title || '智能体用户管理系统'
  
  // 检查是否需要认证
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const token = getToken()
  const tokenExpire = localStorage.getItem('tokenExpire') || sessionStorage.getItem('tokenExpire')
  
  let isExpired = false
  if (tokenExpire) {
    const expireTime = parseInt(tokenExpire)
    // 检查是否过期。expireTime 是一个时间戳（毫秒）
    if (!isNaN(expireTime) && Date.now() > expireTime) {
      isExpired = true
      // 清理已过期的 token
      localStorage.removeItem('token')
      localStorage.removeItem('tokenExpire')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('tokenExpire')
    }
  }

  if (requiresAuth && (!token || isExpired)) {
    // 未登录或 token 过期，访问需要认证的页面，跳转到登录页，并记录来源路径
    if (isExpired) {
      Vue.prototype.$message && Vue.prototype.$message.warning('登录已过期，请重新登录')
    }
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  } else if (to.path === '/login' && token && !isExpired) {
    // 已登录且访问登录页，跳转到首页
    next({ path: '/' })
  } else if (requiresAuth && !checkPermission(to)) {
    // 已登录但无权访问当前页面
    if (to.path === '/dashboard') {
      next({ path: '/profile', replace: true })
    } else {
      next({ path: '/403', replace: true })
    }
  } else {
    // 其他情况直接放行
    next()
  }
})

export default router
